<template>
  <!-- Popover container ensures toasts appear above dialog ::backdrop (top layer) -->
  <div ref="toasterPopover" popover="manual" class="toaster-popover">
    <Toaster position="bottom-right" :style="{ fontFamily: 'IBM Plex Sans' }" rich-colors />
  </div>
  <template v-if="!isIdentity">
    <CommandPalette />
    <QuickCreateModal />
    <OrganizationEnrichModal />
    <KeyboardShortcutsModal :show="showShortcuts" @close="showShortcuts = false" />
    <ChatPanel />
    <FileViewer />
    <TheHeader v-if="showHeader" />
  </template>
  <main :class="{ 'is-identity': isIdentity, 'is-unresolved': !routeResolved }">
    <router-view />
  </main>
  <TheFooter v-if="!isIdentity && showFooter" />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue"
import { useRoute } from "vue-router"
import { toast, Toaster } from "vue-sonner"
import { storeToRefs } from "pinia"
import ChatPanel from "@/components/app/ChatPanel.vue"
import CommandPalette from "@/components/app/CommandPalette.vue"
import KeyboardShortcutsModal from "@/components/app/KeyboardShortcutsModal.vue"
import TheFooter from "@/components/app/TheFooter.vue"
import TheHeader from "@/components/app/TheHeader.vue"
import FileViewer from "@/components/files/FileViewer.vue"
import OrganizationEnrichModal from "@/components/records/OrganizationEnrichModal.vue"
import QuickCreateModal from "@/components/records/QuickCreateModal.vue"
import { useChat } from "@/composables/useChat"
import { useCommandPalette } from "@/composables/useCommandPalette"
import { initGoogle } from "@/composables/useGoogle"
import { type Shortcut, useMousetrap } from "@/composables/useMousetrap"
import { useQuickCreate } from "@/composables/useQuickCreate"
import { useUploadToast } from "@/composables/useUploadToast"
import { client } from "@/init/apollo"
import { IDENTITY_PATH_PREFIX } from "@/init/posthogSanitize"
import { useUserStore } from "@/stores/user"

const route = useRoute()
const { addShortcuts, removeShortcuts } = useMousetrap()

const userStore = useUserStore()
const { hadAuthenticatedSession } = storeToRefs(userStore)

const toasterPopover = ref<HTMLElement>()
const showShortcuts = ref(false)
const quickCreate = useQuickCreate({ client, toast })
const commandPalette = useCommandPalette()
useUploadToast()

const { isOpen: aiPanelOpen } = useChat({ client })

const isFixedLayout = computed(() => route.matched.some((r) => r.meta.fixedLayout))
// Heavybit Identity pages (/identity/*) serve people who may have no Auger
// account: no header, palette, modals, chat, user identify, or Google init.
//
// FOUC FIX: main.ts mounts the app WITHOUT awaiting router.isReady() (see its
// comment) — deliberately, since that await would delay first paint on every
// route, not just identity ones. That means on the very first synchronous
// render, before the router has resolved anything, `route.matched` is `[]`
// (vue-router's START_LOCATION_NORMALIZED) and `route.meta` is `{}`. Reading
// route.meta.identity alone during that window always answers false, which
// is exactly the bug this fixes: on an /identity/* URL the Auger header,
// footer, palette, modals and chat all rendered for one frame before
// disappearing once routing settled.
//
// route.matched.length === 0 is that "not resolved yet" signal — it is only
// ever empty before the FIRST navigation resolves; every completed
// navigation after that populates it, even for a route with no nested
// records. While it's empty, window.location.pathname is used instead: it's
// available synchronously from the browser, before Vue or the router have
// run at all, so it's the one thing that can answer this correctly on the
// very first render. Once the router is live, route.meta.identity is the
// authoritative source, exactly as before — this is a fallback for the
// unresolved window only, not a second source of truth that could drift from
// the route table.
// The same "not resolved yet" signal the comment above describes, named so it
// can gate more than the identity check. The window it covers is not brief on a
// cold load: the router guard awaits auth.ready(), which dynamically imports the
// Stytch SDK, before it can decide whether to redirect to sign-in.
const routeResolved = computed(() => route.matched.length > 0)

const isIdentity = computed(() => {
  if (!routeResolved.value) return window.location.pathname.startsWith(IDENTITY_PATH_PREFIX)
  return !!route.meta.identity
})
// Gates the header on an authenticated session, which is also what keeps it off
// the screen before sign-in. This was a `:class="{ 'is-invisible': ... }"`
// binding with no rule behind it anywhere in the repo, so it hid nothing: a
// private window showed the full Auger header for as long as the SDK took to
// load, then redirected. `v-if` matches TheFooter's gate on the next line.
const showHeader = computed(() => hadAuthenticatedSession.value)
const showFooter = computed(() => showHeader.value && !isFixedLayout.value)

watchEffect(() => {
  document.body.classList.toggle("fixed-layout", isFixedLayout.value)
  document.body.classList.toggle("ai-panel-open", aiPanelOpen.value)
})

let toasterObserver: MutationObserver | undefined

const shortcuts: Shortcut[] = [
  {
    keys: ["command+k", "ctrl+k", "/"],
    group: "Global",
    label: "Open command palette",
    fn: () => commandPalette.open(),
  },
  {
    keys: "+",
    group: "Global",
    label: "Quick create a record",
    fn: () => quickCreate.open(),
  },
  {
    keys: "?",
    group: "Global",
    label: "Show keyboard shortcuts",
    fn: () => (showShortcuts.value = true),
  },
]

// Everything the Auger shell needs and an identity page must not do: boot the
// Google client and bind the global shortcuts. Deferred rather than skipped,
// because with auth disabled locally a person can navigate from
// /identity/done into Auger without a page load.
//
// Resolving the signed-in user is NOT here: the router guard calls
// userState.identify() (idempotent) on the first non-identity route, which is
// earlier than this watcher and also covers an in-SPA continuation from
// /identity/callback to the target route.
let shellStarted = false
function startAugerShell() {
  if (shellStarted || isIdentity.value) return
  shellStarted = true
  initGoogle().catch((err: any) => {
    console.warn("Google initialization failed:", err)
  })
  addShortcuts(shortcuts)
}
watch(isIdentity, startAugerShell, { immediate: true })

onMounted(() => {
  // The toaster is the one piece of chrome an identity page may still raise,
  // and it is fixed-positioned, so it is wired up on every route.
  const popover = toasterPopover.value
  const toaster = popover?.querySelector("[data-sonner-toaster]")
  if (toaster) {
    toasterObserver = new MutationObserver((mutations) => {
      const hasAdditions = mutations.some((m) => m.addedNodes.length > 0)
      if (!hasAdditions) return

      if (popover!.matches(":popover-open")) {
        popover!.hidePopover()
      }
      popover!.showPopover()
    })
    toasterObserver.observe(toaster, { childList: true })
  }
})

onBeforeUnmount(() => {
  toasterObserver?.disconnect()

  if (shellStarted) removeShortcuts(shortcuts)
})
</script>

<style>
@import "@auger/ui/styles/index.css";
@import "vue-sonner/style.css";

/* Toaster popover: uses top layer to appear above dialog ::backdrop */
.toaster-popover {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  overflow: visible;
  position: fixed;
  inset: auto;
  bottom: 0;
  right: 0;
}

/* Heavybit Identity pages render no header and no footer, so <main> is the
   whole page. Drop the shell's bottom padding (which would push the viewport
   into a scrollbar) and let IdentityFrame fill the box instead of asking for
   a second viewport height of its own.

   background: base.css's `body > main` rule paints every <main> with
   --auger-bg-subtle — a light, near-white surface, correct for the rest of
   the app. An identity route's own content (IdentityFrame.vue) is dark, but
   IdentityFrame is behind a lazy `import()` (router/index.ts), so there's a
   real window — the chunk fetch — where <main class="is-identity"> exists
   with nothing inside it yet. Without this override that window flashes the
   light --auger-bg-subtle in a page that is dark everywhere else, including
   <body> itself (index.html sets it inline, before any CSS loads, so body
   never flashes). --identity-bg (tokens.css) is the same fixed dark value
   IdentityFrame.vue itself paints — see that token's comment for why it's a
   NEW value rather than an --auger-* token override. */
main.is-identity {
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  background: var(--identity-bg);
}

/* The same flash, one layer down. base.css paints `body > main` with
   --auger-bg-subtle (a light, near-white surface) and gives it flex-grow: 1,
   so it fills the viewport. Before the first navigation resolves there is
   nothing inside it, and on a cold load that window lasts as long as the
   router guard's auth.ready() takes to import the Stytch SDK — so an
   unauthenticated visitor got a full-viewport light page, over <body>'s dark
   slate, until the redirect to sign-in fired.

   Transparent rather than a colour: <body> is already --auger-dark (base.css)
   and index.html paints the same value inline before any CSS exists, so
   letting it show through means the page never changes colour at all between
   first paint and the redirect. */
main.is-unresolved {
  background: transparent;
}

/* Fixed layout mode: header + main fill viewport exactly, main scrolls internally */
body.fixed-layout {
  height: 100vh;
  /* Mobile browsers' 100vh includes the area behind the collapsing toolbar,
     which hides the bottom of the page; dvh tracks what's actually visible. */
  height: 100dvh;
  min-height: 0;
}

body.fixed-layout main {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  /* `body > main` reserves 4rem for the footer (base.css). A fixedLayout route
     renders no footer — showFooter is `showHeader && !isFixedLayout` — so that
     reservation is left as an empty strip along the bottom of every admin,
     events and pipeline page. Identity pages zero it for the same reason, in
     main.is-identity above. */
  padding-bottom: 0;
}
</style>
